import apiClient from './apiService';

export interface Project {
    id: number; // Changed to number
    entity_id?: number;
    project_code: string;
    name: string;
    description?: string;
    grc_audit_id?: string;
    grc_risk_id?: string;
    project_manager_id?: number; // Changed to number
    baseline_start_date?: string;
    baseline_finish_date?: string;
    status: 'DRAFT' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';
    budget_amount?: number | null;
    workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
    is_approved?: number;
    created_by?: number;
    created_on?: string;
    modified_by?: number;
    modified_on?: string;
    // System-generated code (e.g. "PRJ-1") — null for drafts, set on submit
    code?: string | null;
}

export interface ProjectCreate {
    project_code: string;
    name: string;
    description?: string;
    grc_audit_id?: string;
    grc_risk_id?: string;
    project_manager_id?: number; // Changed to number
    baseline_start_date?: string;
    baseline_finish_date?: string;
    status?: 'DRAFT' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';
    budget_amount?: number | null;
}

export interface ProjectUpdate {
    name?: string;
    description?: string;
    grc_audit_id?: string;
    grc_risk_id?: string;
    project_manager_id?: number; // Changed to number
    baseline_start_date?: string;
    baseline_finish_date?: string;
    status?: 'DRAFT' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';
    budget_amount?: number | null;
}

export const getProjects = async (page = 1, limit = 20) => {
    const response = await apiClient.get<{ data: Project[], meta: any }>(`/api/v1/prm/projects?page=${page}&limit=${limit}`);
    return response.data;
};

// Projects where the caller is the lead (head) or an active resource — the
// staff "My Projects" list in the Action Centre.
export const getMyProjects = async (page = 1, limit = 100) => {
    const response = await apiClient.get<{ data: Project[], meta: any }>(`/api/v1/prm/projects?mine=true&page=${page}&limit=${limit}`);
    return response.data;
};

export const getProject = async (id: string | number) => {
    const response = await apiClient.get<Project>(`/api/v1/prm/projects/${id}`);
    return response.data;
};

export const createProject = async (data: ProjectCreate, action: 'draft' | 'submit' | 'approve' = 'draft') => {
    const response = await apiClient.post<Project>(`/api/v1/prm/projects?action=${action}`, data);
    return response.data;
};

export const updateProject = async (id: string | number, data: ProjectUpdate) => {
    const response = await apiClient.put<Project>(`/api/v1/prm/projects/${id}`, data);
    return response.data;
};

// ---------- Insights ----------
export interface PrmCount {
    label: string;
    count: number;
}

export interface PrmPerProject {
    label: string;
    open_count: number;
    overdue_count: number;
}

export interface PrmSpotlightTask {
    task_name: string;
    project_name: string;
    due_date: string | null;
    priority: string | null;
    status: string;
    is_milestone: boolean;
    is_overdue: boolean;
}

export interface PrmInsights {
    overdue_tasks: number;
    due_week: number;
    due_month: number;
    critical_pending: number;
    open_tasks: number;
    total_tasks: number;
    completion_rate: number;
    active_projects: number;
    projects_by_status: PrmCount[];
    tasks_by_status: PrmCount[];
    tasks_by_priority: PrmCount[];
    per_project: PrmPerProject[];
    spotlight: PrmSpotlightTask[];
}

export const prmInsightsApi = {
    get: async (): Promise<PrmInsights> => {
        const resp = await apiClient.get('/api/v1/prm/insights');
        return resp.data?.data;
    },
};

export const projectService = {
    getProjects,
    getMyProjects,
    getProject,
    createProject,
    updateProject
};
